import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowUpDown, Search } from "lucide-react";

export const PILL = {
  aktif: ["bg-emerald-50 text-emerald-700 border-emerald-200", "bg-emerald-500", "Aktif"],
  tamamlandi: ["bg-blue-50 text-blue-700 border-blue-200", "bg-blue-500", "Tamamlandı"],
  bekliyor: ["bg-amber-50 text-amber-700 border-amber-200", "bg-amber-500", "Bekliyor"],
  eksik: ["bg-orange-50 text-orange-700 border-orange-200", "bg-orange-500", "Eksik"],
  gecikmis: ["bg-red-50 text-red-700 border-red-200", "bg-red-500", "Gecikmiş"],
  basarisiz: ["bg-zinc-100 text-zinc-700 border-zinc-300", "bg-zinc-500", "Başarısız"],
  planlandi: ["bg-sky-50 text-sky-700 border-sky-200", "bg-sky-500", "Planlandı"],
  iptal: ["bg-zinc-100 text-zinc-500 border-zinc-200", "bg-zinc-400", "İptal"],
  notr: ["bg-zinc-50 text-zinc-600 border-zinc-200", "bg-zinc-400", "-"],
};

export const Pill = ({ s, children, testid }) => {
  const [cls, dot, label] = PILL[s] || PILL.notr;
  return <span data-testid={testid} className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${cls}`}><span className={`h-1.5 w-1.5 rounded-full ${dot}`} />{children || label}</span>;
};

export const Header = ({ title, sub, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-3 mb-6" data-testid="page-header">
    <div><h1 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900">{title}</h1>{sub && <p className="text-sm text-zinc-500 mt-1">{sub}</p>}</div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

const TONES = { red: "bg-red-50 text-[#dc2626]", green: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600", blue: "bg-blue-50 text-blue-600", violet: "bg-violet-50 text-violet-600", zinc: "bg-zinc-100 text-zinc-700", orange: "bg-orange-50 text-orange-600" };

export const Kpi = ({ icon: Icon, label, value, foot, tone = "red", testid, to, onClick }) => {
  const C = onClick ? "button" : "div";
  return (
    <C onClick={onClick} className="pro-card p-4 sm:p-5 flex items-center gap-4 text-left hover:shadow-md hover:-translate-y-0.5" style={{ transition: "box-shadow .2s, transform .2s" }} data-testid={testid}>
      <span className={`grid place-items-center h-12 w-12 shrink-0 rounded-xl ${TONES[tone]}`}><Icon size={22} /></span>
      <span className="min-w-0"><span className="block text-[11px] font-bold uppercase tracking-wider text-zinc-500">{label}</span>
        <span className="block text-2xl font-black tracking-tight text-zinc-900 truncate" data-testid={testid && `${testid}-value`}>{value}</span>
        {foot && <span className="block text-xs text-zinc-500 truncate">{foot}</span>}</span>
    </C>
  );
};

export const Card = ({ title, action, children, className = "", testid }) => (
  <section className={`pro-card ${className}`} data-testid={testid}>
    {title && <div className="flex items-center justify-between gap-2 px-5 pt-4 pb-3 border-b border-zinc-100"><h2 className="font-bold text-zinc-900">{title}</h2>{action}</div>}
    <div className="p-5">{children}</div>
  </section>
);

export const Tabs = ({ tabs, value, onChange, testid = "tabs" }) => (
  <div className="flex gap-1 overflow-x-auto border-b border-zinc-200 mb-4" data-testid={testid} role="tablist">
    {tabs.map(([k, l, n]) => <button key={k} type="button" role="tab" aria-selected={value === k} onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
      className={`shrink-0 px-4 py-2.5 text-sm border-b-2 -mb-px flex items-center gap-2 ${value === k ? "border-[#dc2626] text-[#dc2626] font-bold bg-red-50/50" : "border-transparent text-zinc-500 font-medium hover:text-zinc-900"}`} style={{ transition: "color .15s, background-color .15s" }}>
      {l}{n !== undefined && <span className={`rounded-full px-2 text-[11px] font-bold ${value === k ? "bg-[#dc2626] text-white" : "bg-zinc-100 text-zinc-600"}`}>{n}</span>}</button>)}
  </div>
);

export const SearchBox = ({ value, onChange, placeholder = "Ara", testid }) => (
  <div className="relative min-w-[200px] flex-1"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
    <input className="field !pl-9" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>
);

export function DataTable({ cols, rows, testid, empty = "Kayıt bulunamadı.", pageSize = 20, rowKey = (r) => r.id }) {
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [rows.length]);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const c = cols.find((x) => x.key === sort.key);
    const v = (r) => (c.sort ? c.sort(r) : "");
    return [...rows].sort((a, b) => (v(a) > v(b) ? 1 : v(a) < v(b) ? -1 : 0) * sort.dir);
  }, [rows, sort, cols]);
  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const p = Math.min(page, pages - 1);
  const view = sorted.slice(p * pageSize, (p + 1) * pageSize);
  const toggle = (k) => setSort(sort?.key === k ? { key: k, dir: -sort.dir } : { key: k, dir: 1 });
  return (
    <div className="pro-card overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto"><table className="w-full text-sm">
        <thead><tr className="bg-zinc-50 border-b border-zinc-200">{cols.map((c) => <th key={c.key} className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-zinc-600 whitespace-nowrap">
          {c.sort ? <button className="inline-flex items-center gap-1 hover:text-zinc-900" onClick={() => toggle(c.key)} data-testid={`${testid}-sort-${c.key}`}>{c.label}<ArrowUpDown size={12} className={sort?.key === c.key ? "text-[#dc2626]" : ""} /></button> : c.label}</th>)}</tr></thead>
        <tbody>{view.length ? view.map((r) => <tr key={rowKey(r)} className="border-b border-zinc-100 hover:bg-zinc-50/80" style={{ transition: "background-color .15s" }}>
          {cols.map((c) => <td key={c.key} className={`px-4 py-3 align-middle ${c.wrap ? "" : "whitespace-nowrap"}`}>{c.render(r)}</td>)}</tr>) : null}</tbody>
      </table></div>
      {!view.length && <p className="px-4 py-10 text-center text-sm text-zinc-500" data-testid={`${testid}-empty`}>{empty}</p>}
      {sorted.length > pageSize && <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-zinc-100 text-sm" data-testid={`${testid}-pagination`}>
        <span className="text-zinc-500">Toplam <b className="text-zinc-900">{sorted.length}</b> kayıt • Sayfa {p + 1}/{pages}</span>
        <div className="flex items-center gap-1">
          <button className="pg-btn" disabled={p === 0} onClick={() => setPage(p - 1)} aria-label="Önceki" data-testid={`${testid}-prev`}><ChevronLeft size={15} /></button>
          {Array.from({ length: pages }, (_, i) => i).filter((i) => i === 0 || i === pages - 1 || Math.abs(i - p) <= 1).map((i, j, arr) => <span key={i} className="flex">
            {j > 0 && i - arr[j - 1] > 1 && <span className="px-1 text-zinc-400">…</span>}
            <button className={`pg-btn ${i === p ? "!bg-[#dc2626] !text-white !border-[#dc2626]" : ""}`} onClick={() => setPage(i)} data-testid={`${testid}-page-${i + 1}`}>{i + 1}</button></span>)}
          <button className="pg-btn" disabled={p >= pages - 1} onClick={() => setPage(p + 1)} aria-label="Sonraki" data-testid={`${testid}-next`}><ChevronRight size={15} /></button>
        </div></div>}
    </div>
  );
}

export const Filters = ({ children, testid = "filters" }) => <div className="pro-card p-4 mb-4 flex flex-wrap items-end gap-3" data-testid={testid}>{children}</div>;
export const FItem = ({ label, children }) => <label className="grid gap-1 text-[11px] font-bold uppercase tracking-wider text-zinc-500 min-w-[150px]">{label}{children}</label>;
